import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column } from '../DataTable';
import EmptyState from '../EmptyState';
import { MetricCard } from '../MetricCards';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useConfirm } from '../ui/ConfirmProvider';
import MergeDuplicatesModal from './MergeDuplicatesModal';
import BulkAddGuestsModal, { type BulkAddSummary } from './BulkAddGuestsModal';
import { useNotifications } from '../NotificationProvider';
import { useRole } from '../../hooks/useRole';
import { usePageFilter } from '../../contexts/PageFilterContext';
import { useProject } from '../../contexts/ProjectContext';
import {
  loadGuests, loadArchivedGuests, refreshGuestsFromApi, refreshGuestTagsFromApi, loadGuestTags, fetchServerGuestStats,
  getGuestStats, archiveGuest, restoreGuest, deleteGuestPermanentOrThrow, didArchivedGuestsLoadFail, type ServerGuestStats,
} from '../../services/GuestService';
import { GUEST_SAVE_WARNINGS_EVENT } from '../../services/GuestContactCheckService';
import { loadProjectRsvps, type ProjectRsvp } from '../../services/GuestEventsService';
import { effectiveRsvpStatus } from '../../utils/guestRsvpStatus';
import { ALL_SIDES, buildSideOptions, matchesSideFilter, sideLabel, normalizeSide } from '../../utils/guestSide';
import { findDuplicateGroups } from '../../utils/guestMerge';
import { guestPath, GUEST_NEW_PATH } from '../../utils/guestRoutes';
import { useExport } from '../header/exporters';
import { useGuestsBar, setGuestsBar, resetGuestsBar, GUEST_MERGE_OPEN, GUEST_BULK_ADD_OPEN } from '../../hooks/useGuestsBar';
import { permanentDeleteErrorMessage, readableErrorMessage } from '../../utils/permanentDeleteError';
import type { Guest } from '../../types/Guests';

type Row = Guest & Record<string, unknown>;

const RSVP_LABEL: Record<string, string> = { confirmed: 'Confirmed', declined: 'Declined', pending: 'Pending', awaiting_plus_one: 'Awaiting plus-one' };

/** Everything the list shows is derived from these inputs, so the filtering rules are testable on their own. */
export interface GuestListFilters { search: string; side: string; rsvp: string[]; tags: string[]; roles: string[]; anyPending?: boolean }

/** `pendingGuestIds`: guests with at least one per-event answer still pending (for the "has any pending" filter). */
export function applyGuestFilters(guests: Guest[], f: GuestListFilters, pendingGuestIds: ReadonlySet<string> = new Set()): Guest[] {
  const q = f.search.trim().toLowerCase();
  return guests.filter(g => {
    if (!matchesSideFilter(g, f.side)) return false;
    if (f.rsvp.length > 0 && !f.rsvp.includes(effectiveRsvpStatus(g))) return false;
    if (f.tags.length > 0 && !f.tags.some(id => g.tags.some(t => t.id === id))) return false;
    if (f.roles.length > 0 && !f.roles.includes(g.role ?? '')) return false;
    if (f.anyPending && !pendingGuestIds.has(g.id)) return false;
    if (!q) return true;
    return [g.name, g.email, g.phone ?? '', g.id, ...g.tags.map(t => t.label)].some(v => v.toLowerCase().includes(q));
  });
}

const GuestManager: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showSuccess, showError, showWarning } = useNotifications();
  const { isPlanner } = useRole();
  const { activeProjectId } = useProject();

  const [active, setActive] = useState<Guest[]>(() => loadGuests());
  const [archived, setArchived] = useState<Guest[]>(() => loadArchivedGuests());
  const [archivedFailed, setArchivedFailed] = useState(() => didArchivedGuestsLoadFail());
  const [serverStats, setServerStats] = useState<ServerGuestStats | null>(null);
  const [rsvps, setRsvps] = useState<ProjectRsvp[]>([]);
  const [loading, setLoading] = useState(() => loadGuests().length === 0);
  const [loadError, setLoadError] = useState<unknown>(null);
  // parts of the page that are secondary to the list itself (totals, tags, answers per event): when one fails the list
  // still shows and a one-line notice names what is missing
  const [partial, setPartial] = useState<string[]>([]);
  // Archived switch and Filters live in the breadcrumb bar (components/header/GuestsBar.tsx); the search is the global
  // header search, which hands its term to the page (DataTable narrows its rows with it too)
  const bar = useGuestsBar();
  const pageSearch = usePageFilter();
  const showArchived = bar.archived;
  const filters: GuestListFilters = useMemo(() => ({ search: pageSearch, side: bar.side, rsvp: bar.rsvp, tags: bar.tags, roles: bar.roles, anyPending: bar.anyPending }), [pageSearch, bar.side, bar.rsvp, bar.tags, bar.roles, bar.anyPending]);
  // Ticked guests are remembered by id, never by table position: sorting, filtering or switching list cannot change who
  // an action reaches, and a guest that is no longer shown is no longer selected.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState(() => loadGuestTags());
  useEffect(() => () => resetGuestsBar(), []);
  useEffect(() => { setSelectedIds(new Set()); }, [showArchived]);

  const reload = useCallback(async () => {
    setLoadError(null);
    try {
      await refreshGuestsFromApi();
      setActive(loadGuests());
      setArchived(loadArchivedGuests());
      setArchivedFailed(didArchivedGuestsLoadFail());
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
    const missing: string[] = [];
    const note = (what: string) => { missing.push(what); setPartial([...missing]); };
    setPartial([]);
    fetchServerGuestStats().then(stats => { if (stats) setServerStats(stats); else note('totals'); });
    refreshGuestTagsFromApi().then(setTags).catch(() => note('tags'));
    loadProjectRsvps().then(setRsvps).catch(() => { setRsvps([]); note('answers per event'); });
  }, []);

  useEffect(() => { void reload(); }, [reload, activeProjectId]);

  // The API returns the duplicate-phone/email notes of a save; show them as a notice, never as an error.
  useEffect(() => {
    const onWarnings = (event: Event) => {
      const detail = (event as CustomEvent<{ guestName: string; messages: string[] }>).detail;
      if (detail?.messages?.length) showWarning(`${detail.guestName}: ${detail.messages.join(' ')} The guest was saved.`);
    };
    window.addEventListener(GUEST_SAVE_WARNINGS_EVENT, onWarnings);
    return () => window.removeEventListener(GUEST_SAVE_WARNINGS_EVENT, onWarnings);
  }, [showWarning]);

  const pool = showArchived ? archived : active;
  const sideOptions = useMemo(() => buildSideOptions(active), [active]);
  const pendingGuestIds = useMemo(() => new Set(rsvps.filter(r => r.rsvp_status === 'pending').map(r => r.guest_ref)), [rsvps]);
  const visible = useMemo(() => applyGuestFilters(pool, filters, pendingGuestIds), [pool, filters, pendingGuestIds]);
  const tagColour = useMemo(() => new Map(tags.map(t => [t.id, t.color])), [tags]);
  const selected = useMemo(() => visible.filter(g => selectedIds.has(g.id)), [visible, selectedIds]);
  const duplicateGroups = useMemo(() => findDuplicateGroups(active).length, [active]);

  // "Invited" is the API's own count: guests whose archived flag is off, whatever their stored status says.
  // The cards follow the Side filter and the Archived switch, but never the RSVP filter (the cards ARE that filter, so
  // clicking one must not change the others). The API's own totals only describe the whole non-archived list, so they are
  // used only when no side is chosen and the active list is showing; otherwise the cards count the narrowed guests.
  const statsGuests = useMemo(() => pool.filter(g => matchesSideFilter(g, bar.side)), [pool, bar.side]);
  const useServerTotals = !showArchived && bar.side === ALL_SIDES;
  const stats = useMemo(() => getGuestStats(statsGuests, undefined, useServerTotals ? serverStats : null), [statsGuests, serverStats, useServerTotals]);

  const rsvpByGuest = useMemo(() => {
    const map = new Map<string, ProjectRsvp[]>();
    for (const r of rsvps) map.set(r.guest_ref, [...(map.get(r.guest_ref) ?? []), r]);
    return map;
  }, [rsvps]);

  const setCard = (status: 'confirmed' | 'pending' | 'declined' | null) =>
    setGuestsBar({ rsvp: status === null || (bar.rsvp.length === 1 && bar.rsvp[0] === status) ? [] : [status] });

  const columns: Column<Row>[] = useMemo(() => [
    { key: 'id', header: 'ID', sortable: true, width: '96px' },
    {
      key: 'name', header: 'Guest', sortable: true,
      render: (_v, g) => (
        <button type="button" className="gst-namelink" onClick={() => navigate(guestPath(g.id)!)}>
          {g.name}{g.is_honoree ? ' ★' : ''}
          {g.party_of && <span className="gst-badge gst-badge--muted">+1</span>}
        </button>
      ),
    },
    { key: 'side', header: 'Side', sortable: true, sortValue: g => normalizeSide(g.side) ?? '', render: (_v, g) => { const s = normalizeSide(g.side); return s ? sideLabel(s) : '—'; } },
    {
      key: 'tags', header: 'Tags', render: (_v, g) => g.tags.length === 0 ? '—' : (
        <span className="gst-tags-inline">{g.tags.map(t => <span key={t.id} className="gst-tag" style={{ /* ds-dynamic: tag colour from the project's tag catalogue */ '--gst-tag-color': tagColour.get(t.id) ?? t.color ?? undefined } as React.CSSProperties}>{t.label}</span>)}</span>
      ),
    },
    {
      key: 'rsvp_status', header: 'RSVP', sortable: true, sortValue: g => effectiveRsvpStatus(g),
      render: (_v, g) => {
        const perEvent = rsvpByGuest.get(g.id) ?? [];
        // only the counts are shown: a bare overall word read as "this whole guest is confirmed". The derived overall
        // answer still drives sorting and the RSVP filter, and is in the tooltip and the accessible name.
        const parts = (['confirmed', 'declined', 'pending'] as const)
          .map(status => ({ status, n: perEvent.filter(r => r.rsvp_status === status).length }))
          .filter(p => p.n > 0);
        const overall = effectiveRsvpStatus(g);
        const detail = [`Overall: ${overall}`, ...perEvent.map(r => `${r.event_title}: ${r.rsvp_status}`)].join('; ');
        if (parts.length === 0) return <span className="gst-rsvp gst-rsvp--none" title={detail} aria-label={`No invitations. ${detail}`}>No invitations</span>;
        return (
          <span className="gst-rsvp" title={detail.split('; ').join('\n')} aria-label={detail}>
            {parts.map((p, i) => (
              <React.Fragment key={p.status}>
                {i > 0 && <span aria-hidden="true"> · </span>}
                <span className={`gst-rsvp__part gst-rsvp__part--${p.status}`}>{p.n} {p.status}</span>
              </React.Fragment>
            ))}
          </span>
        );
      },
    },
    {
      key: 'phone', header: 'Contact',
      render: (_v, g) => (
        <span className="gst-contact">
          <span className="gst-contact__phone">{g.phone || '—'}</span>
          <span className="gst-contact__email" title={g.email || undefined}>{g.email || '—'}</span>
        </span>
      ),
    },
  ], [navigate, rsvpByGuest, tagColour]);

  useExport('guests', {
    rows: visible.map(g => ({
      id: g.id, name: g.name, side: g.side, tags: g.tags, rsvp: effectiveRsvpStatus(g), email: g.email, phone: g.phone,
      plus_ones: g.plus_ones, notes: g.notes, archived: g.archived,
    })),
    ctx: undefined,
  });

  const bulk = async (label: string, action: (g: Guest) => Promise<void>, failed: (err: unknown) => string) => {
    setBusy(true);
    let ok = 0;
    const errors: string[] = [];
    for (const g of selected) {
      try { await action(g); ok += 1; } catch (err) { errors.push(`${g.name}: ${failed(err)}`); }
    }
    setBusy(false);
    setSelectedIds(new Set());
    setActive(loadGuests());
    setArchived(loadArchivedGuests());
    fetchServerGuestStats().then(setServerStats);
    if (ok > 0) showSuccess(`${label} ${ok} guest${ok === 1 ? '' : 's'}.`);
    if (errors.length > 0) showError(errors.slice(0, 3).join(' '));
  };

  const onBulkArchive = async () => {
    if (!(await confirm({ title: `Archive ${selected.length} guest${selected.length === 1 ? '' : 's'}?`, description: 'They leave the guest list and can be restored from Archived.', confirmLabel: 'Archive' }))) return;
    await bulk('Archived', g => archiveGuest(g.id), e => readableErrorMessage(e, 'could not be archived.'));
  };
  const onBulkRestore = () => bulk('Restored', g => restoreGuest(g.id), e => readableErrorMessage(e, 'could not be restored.'));
  const onBulkDelete = async () => {
    if (!(await confirm({
      title: `Delete ${selected.length} guest${selected.length === 1 ? '' : 's'} forever?`, variant: 'danger', confirmLabel: 'Delete forever',
      consequences: ['The guests and their answers are removed for good.'],
    }))) return;
    await bulk('Deleted', g => deleteGuestPermanentOrThrow(g.id), e => permanentDeleteErrorMessage(e, 'guest'));
  };

  const onImported = (summary: BulkAddSummary) => {
    if (summary.created > 0) showSuccess(`Added ${summary.created} guest${summary.created === 1 ? '' : 's'}.`);
    if (summary.notes.length > 0) (summary.failed > 0 ? showError : showWarning)(summary.notes.slice(0, 4).join(' '));
    void reload();
  };

  // what the breadcrumb bar needs from this list to draw its Filters and the merge badge
  useEffect(() => {
    setGuestsBar({ sideOptions, tagOptions: tags.map(t => ({ value: t.id, label: t.label })), duplicateGroups });
  }, [sideOptions, tags, duplicateGroups]);

  // the bar's Merge duplicates and Bulk add icon buttons
  useEffect(() => {
    const openMerge = () => setMergeOpen(true);
    const openBulk = () => setBulkOpen(true);
    window.addEventListener(GUEST_MERGE_OPEN, openMerge);
    window.addEventListener(GUEST_BULK_ADD_OPEN, openBulk);
    return () => { window.removeEventListener(GUEST_MERGE_OPEN, openMerge); window.removeEventListener(GUEST_BULK_ADD_OPEN, openBulk); };
  }, []);

  return (
    <div className="gst-page fade-in">
      <section className="stats-grid" aria-label="Guest totals">
        <MetricCard label="Total guests" value={stats.totalGuests} tone="blue" icon="users"
          active={filters.rsvp.length === 0} onClick={() => setCard(null)} title="Guests on the list, archived ones not counted" />
        <MetricCard label="Confirmed" value={stats.confirmedGuests} tone="green" icon="check"
          active={filters.rsvp.length === 1 && filters.rsvp[0] === 'confirmed'} onClick={() => setCard('confirmed')} />
        <MetricCard label="Pending" value={stats.pendingRSVPs} tone="orange" icon="clock"
          active={filters.rsvp.length === 1 && filters.rsvp[0] === 'pending'} onClick={() => setCard('pending')} />
        <MetricCard label="Declined" value={stats.declinedGuests} tone="red" icon="x"
          active={filters.rsvp.length === 1 && filters.rsvp[0] === 'declined'} onClick={() => setCard('declined')} />
        <MetricCard label="Plus-ones" value={stats.totalPlusOnes} tone="purple" icon="user" />
      </section>

      {selected.length > 0 && isPlanner && (
        <div className="gst-selection" role="region" aria-label="Selected guests">
          <span>{selected.length} selected</span>
          {!showArchived && <Button size="sm" variant="outline" disabled={busy} onClick={onBulkArchive}>Archive</Button>}
          {showArchived && <Button size="sm" variant="outline" disabled={busy} onClick={onBulkRestore}>Restore</Button>}
          {showArchived && <Button size="sm" variant="danger" disabled={busy} onClick={onBulkDelete}>Delete forever</Button>}
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {Boolean(loadError) && active.length === 0 && archived.length === 0 && (
        <LoadErrorBox what="guests" error={loadError} onRetry={() => { setLoading(true); void reload(); }} />
      )}
      {Boolean(loadError) && (active.length > 0 || archived.length > 0) && (
        <p className="gst-notice" role="status">
          Couldn't refresh the guest list, so this is the last list that loaded.{' '}
          <button type="button" className="gst-notice__retry" onClick={() => void reload()}>Retry</button>
        </p>
      )}
      {partial.length > 0 && !loadError && (
        <p className="gst-notice" role="status">Couldn't load {partial.join(', ')}. The guest list is complete.</p>
      )}
      {showArchived && archivedFailed && !loadError && (
        <LoadErrorBox what="archived guests" error="The archived list could not be loaded." onRetry={() => void reload()} />
      )}

      {!loading && visible.length === 0 && !(loadError && pool.length === 0) ? (
        <EmptyState
          icon="users"
          title={showArchived ? 'No archived guests' : pool.length === 0 ? 'No guests yet' : 'No guests match these filters'}
          description={showArchived ? undefined : pool.length === 0 ? 'Add your first guest, or paste a list with Bulk add.' : 'Clear a filter to see more guests.'}
          action={!showArchived && pool.length === 0 && isPlanner ? <Button variant="primary" onClick={() => navigate(GUEST_NEW_PATH)}>New guest</Button> : undefined}
        />
      ) : (
        <DataTable<Row>
          data={visible as Row[]}
          columns={columns}
          loading={loading}
          {...(isPlanner ? { rowKey: (g: Row) => g.id, selectedKeys: selectedIds, onSelectedKeysChange: setSelectedIds } : {})}
          pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
          emptyMessage="No guests"
        />
      )}

      <MergeDuplicatesModal open={mergeOpen} onClose={() => setMergeOpen(false)} guests={active}
        onMerged={() => { setMergeOpen(false); showSuccess('Guests merged.'); void reload(); }} />
      <BulkAddGuestsModal open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={onImported} />
    </div>
  );
};

export default GuestManager;
